import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import {
  SERVICES, CONDITIONS, URGENCY_OPTIONS, ADDONS_CATALOG, PHOTO_REQUIRED,
  SIZE_OPTIONS, RESTAURANT_SECTIONS, PRESSURE_SUB, JUNK_LOADS, WINDOW_TYPES,
  CARPET_ITEMS, ADDON_GROUPS_FOR_SERVICE, FLOORS_APPLICABLE,
} from "../data/services";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, ArrowRight, Upload, X, CheckCircle2, Users, Clock, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

const TOTAL_STEPS = 8;
const STEP_LABELS = ["Service", "Details", "Condition", "Add-ons", "Photos", "Schedule", "Contact", "Review"];

const DEFAULT_FORM = {
  service: "",
  details: {},
  addons: [],
  condition: "standard",
  floors: 1,
  urgency: "regular",
  preferred_date: "",
  preferred_time: "",
  contact: { name: "", email: "", phone: "", address: "", borough: "", notes: "" },
  photos: [],
};

const BOROUGHS = ["Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island"];

export default function BookingWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    const s = params.get("service");
    return s && SERVICES.some((sv) => sv.id === s) ? { ...DEFAULT_FORM, service: s } : DEFAULT_FORM;
  });
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateDetail = (k, v) => setForm((f) => ({ ...f, details: { ...f.details, [k]: v } }));
  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleAddon = (id) =>
    setForm((f) => ({ ...f, addons: f.addons.includes(id) ? f.addons.filter((a) => a !== id) : [...f.addons, id] }));

  // Live pricing from server
  const fetchQuote = useCallback(async () => {
    if (!form.service) return;
    setQuoteLoading(true);
    try {
      const { data } = await api.post("/pricing/calculate", {
        service: form.service,
        details: form.details,
        addons: form.addons,
        condition: form.condition,
        floors: form.floors,
        urgency: form.urgency,
      });
      setQuote(data);
    } catch (e) {
      // swallow; show nothing
    } finally {
      setQuoteLoading(false);
    }
  }, [form.service, form.details, form.addons, form.condition, form.floors, form.urgency]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 250);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const photoRequired = useMemo(
    () => PHOTO_REQUIRED.has(form.service) || form.condition === "extreme",
    [form.service, form.condition]
  );

  // Step validation
  const canNext = useMemo(() => {
    if (step === 1) return !!form.service;
    if (step === 2) return stepTwoValid(form);
    if (step === 3) return !!form.condition;
    if (step === 4) return true;
    if (step === 5) return !photoRequired || form.photos.length > 0;
    if (step === 6) return !!form.preferred_date && !!form.preferred_time;
    if (step === 7) {
      const c = form.contact;
      return c.name.trim() && /.+@.+\..+/.test(c.email) && c.phone.trim().length >= 7 && c.address.trim();
    }
    return true;
  }, [step, form, photoRequired]);

  const handlePhotos = async (files) => {
    const arr = Array.from(files).slice(0, 8 - form.photos.length);
    const toAdd = [];
    for (const f of arr) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 4MB`);
        continue;
      }
      const b64 = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(f);
      });
      toAdd.push({ name: f.name, data: b64 });
    }
    setForm((f) => ({ ...f, photos: [...f.photos, ...toAdd].slice(0, 8) }));
  };

  const removePhoto = (idx) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        photos: form.photos.map((p) => p.data),
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Booking request received!");
      navigate(`/booking-success/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SiteHeader />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="mb-10">
          <div className="eyebrow mb-3">Step {step} of {TOTAL_STEPS}</div>
          <h1 className="font-display text-3xl md:text-5xl font-light text-[#0A1128] tracking-tight">
            {STEP_LABELS[step - 1]}
          </h1>
        </div>

        <StepDots step={step} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-8">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 md:p-10 fade-up" key={step}>
              {step === 1 && <StepService form={form} updateForm={updateForm} />}
              {step === 2 && <StepDetails form={form} updateDetail={updateDetail} updateForm={updateForm} />}
              {step === 3 && <StepCondition form={form} updateForm={updateForm} />}
              {step === 4 && <StepAddons form={form} toggleAddon={toggleAddon} />}
              {step === 5 && (
                <StepPhotos
                  form={form}
                  photoRequired={photoRequired}
                  handlePhotos={handlePhotos}
                  removePhoto={removePhoto}
                />
              )}
              {step === 6 && <StepSchedule form={form} updateForm={updateForm} />}
              {step === 7 && <StepContact form={form} updateForm={updateForm} />}
              {step === 8 && <StepReview form={form} quote={quote} />}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                data-testid="wizard-back-btn"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="btn-outline-navy inline-flex items-center gap-2 px-6 py-3 rounded-sm text-sm font-medium disabled:opacity-40"
              >
                <ArrowLeft size={16} /> Back
              </button>

              {step < TOTAL_STEPS ? (
                <button
                  data-testid="wizard-next-btn"
                  onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
                  disabled={!canNext}
                  className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-sm text-sm"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  data-testid="wizard-submit-btn"
                  onClick={submitBooking}
                  disabled={submitting}
                  className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-sm text-sm"
                >
                  {submitting ? "Submitting..." : "Confirm booking request"}
                </button>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4" data-testid="quote-summary">
            <QuoteSummary quote={quote} loading={quoteLoading} form={form} />
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

/* --------------- Step sub-components --------------- */

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="wizard-steps">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const n = i + 1;
        const cls = n === step ? "active" : n < step ? "done" : "";
        return (
          <React.Fragment key={n}>
            <span className={`step-dot ${cls}`} data-testid={`step-dot-${n}`}>
              {n < step ? "✓" : n}
            </span>
            {n < TOTAL_STEPS && <span className="h-px w-6 md:w-10 bg-[#E2E8F0]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepService({ form, updateForm }) {
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">Pick a service</h3>
      <p className="text-sm text-[#64748B] mt-2">Select the category that matches your project.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
        {SERVICES.map((s) => {
          const active = form.service === s.id;
          return (
            <button
              key={s.id}
              data-testid={`select-service-${s.id}`}
              onClick={() => updateForm({ service: s.id, details: {}, addons: [] })}
              className={`text-left p-5 rounded-sm border transition-all duration-200 ${
                active
                  ? "border-[#D4AF37] bg-[#FFFBEA] shadow-[0_6px_20px_rgba(212,175,55,0.18)]"
                  : "border-[#E2E8F0] bg-white hover:border-[#D4AF37]"
              }`}
            >
              <div className="flex items-center gap-3">
                <i className={`fa-solid ${s.icon} text-[#D4AF37]`} />
                <span className="font-display text-base text-[#0A1128]">{s.label}</span>
              </div>
              <div className="text-xs text-[#64748B] mt-2">{s.blurb}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function stepTwoValid(form) {
  const s = form.service;
  const d = form.details;
  if (["apartment", "house", "family_house", "medical_disinfection", "school", "post_construction", "move"].includes(s))
    return !!d.size || (!!d.sqft_bracket && s === "family_house");
  if (s === "office") return !!d.size || Number(d.sqft) > 0;
  if (s === "restaurant") return !!d.section;
  if (s === "pressure_washing") return !!d.subservice;
  if (s === "junk_removal") return !!d.load;
  if (s === "windows") return Number(d.count) > 0 && !!d.window_type;
  if (s === "carpet") return d.items && Object.values(d.items).some((v) => Number(v) > 0);
  return false;
}

function StepDetails({ form, updateDetail, updateForm }) {
  const s = form.service;

  const SizeSelect = ({ optsKey, labelText = "Size" }) => (
    <Field label={labelText}>
      <select
        data-testid="detail-size-select"
        className="pg-input"
        value={form.details.size || ""}
        onChange={(e) => updateDetail("size", e.target.value)}
      >
        <option value="">Select…</option>
        {SIZE_OPTIONS[optsKey].map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </Field>
  );

  return (
    <div className="space-y-6">
      <h3 className="font-display text-xl text-[#0A1128]">Tell us more about your {SERVICES.find((x) => x.id === s)?.label}</h3>

      {s === "apartment" && <SizeSelect optsKey="apartment" />}
      {s === "house" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SizeSelect optsKey="house" labelText="Bedrooms" />
          <Field label="Bathrooms">
            <input type="number" min={1} className="pg-input" data-testid="detail-bathrooms"
              value={form.details.bathrooms || ""}
              onChange={(e) => updateDetail("bathrooms", e.target.value)} />
          </Field>
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "family_house" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Square footage">
            <select
              data-testid="detail-sqft-bracket"
              className="pg-input"
              value={form.details.sqft_bracket || ""}
              onChange={(e) => updateDetail("sqft_bracket", e.target.value)}
            >
              <option value="">Select…</option>
              {SIZE_OPTIONS.family_house.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </Field>
          <Field label="Number of rooms">
            <input type="number" min={1} className="pg-input" data-testid="detail-rooms"
              value={form.details.rooms || ""}
              onChange={(e) => updateDetail("rooms", e.target.value)} />
          </Field>
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "office" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SizeSelect optsKey="office" labelText="Office size" />
          <Field label="Or enter exact square footage (optional)">
            <input type="number" min={0} className="pg-input" data-testid="detail-office-sqft"
              value={form.details.sqft || ""}
              onChange={(e) => updateDetail("sqft", e.target.value)} />
          </Field>
          <Field label="Number of rooms">
            <input type="number" min={0} className="pg-input"
              value={form.details.rooms || ""}
              onChange={(e) => updateDetail("rooms", e.target.value)} />
          </Field>
          <Field label="Bathrooms">
            <input type="number" min={0} className="pg-input"
              value={form.details.bathrooms || ""}
              onChange={(e) => updateDetail("bathrooms", e.target.value)} />
          </Field>
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "restaurant" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Service section">
            <select data-testid="detail-restaurant-section" className="pg-input"
              value={form.details.section || ""}
              onChange={(e) => updateDetail("section", e.target.value)}>
              <option value="">Select…</option>
              {RESTAURANT_SECTIONS.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </Field>
          <Field label="Windows (approx)">
            <input type="number" min={0} className="pg-input"
              value={form.details.windows || ""}
              onChange={(e) => updateDetail("windows", e.target.value)} />
          </Field>
        </div>
      )}
      {s === "school" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SizeSelect optsKey="school" labelText="Campus size" />
          <Field label="Number of classrooms">
            <input type="number" min={0} className="pg-input"
              value={form.details.classrooms || ""}
              onChange={(e) => updateDetail("classrooms", e.target.value)} />
          </Field>
          <Field label="Restrooms">
            <input type="number" min={0} className="pg-input"
              value={form.details.restrooms || ""}
              onChange={(e) => updateDetail("restrooms", e.target.value)} />
          </Field>
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "pressure_washing" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Type of pressure washing">
            <select data-testid="detail-pressure-sub" className="pg-input"
              value={form.details.subservice || ""}
              onChange={(e) => updateDetail("subservice", e.target.value)}>
              <option value="">Select…</option>
              {PRESSURE_SUB.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </Field>
          <Field label="Extras">
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={!!form.details.mold}
                onChange={(e) => updateDetail("mold", e.target.checked)} />
              Mold present
            </label>
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={!!form.details.chemical}
                onChange={(e) => updateDetail("chemical", e.target.checked)} />
              Chemical wash needed
            </label>
          </Field>
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "junk_removal" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Load size">
            <select data-testid="detail-junk-load" className="pg-input"
              value={form.details.load || ""}
              onChange={(e) => updateDetail("load", e.target.value)}>
              <option value="">Select…</option>
              {JUNK_LOADS.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </Field>
          <Field label="Type / description of debris">
            <input type="text" className="pg-input"
              value={form.details.debris_type || ""}
              onChange={(e) => updateDetail("debris_type", e.target.value)} />
          </Field>
        </div>
      )}
      {s === "medical_disinfection" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SizeSelect optsKey="medical_disinfection" labelText="Area size" />
          <Field label="Service level notes">
            <input type="text" className="pg-input"
              value={form.details.level || ""}
              onChange={(e) => updateDetail("level", e.target.value)} />
          </Field>
        </div>
      )}
      {s === "windows" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Number of windows">
            <input type="number" min={1} className="pg-input" data-testid="detail-window-count"
              value={form.details.count || ""}
              onChange={(e) => updateDetail("count", e.target.value)} />
          </Field>
          <Field label="Window type">
            <select data-testid="detail-window-type" className="pg-input"
              value={form.details.window_type || ""}
              onChange={(e) => updateDetail("window_type", e.target.value)}>
              <option value="">Select…</option>
              {WINDOW_TYPES.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
            </select>
          </Field>
        </div>
      )}
      {s === "carpet" && (
        <div>
          <div className="text-sm text-[#64748B] mb-3">Add quantity for each item you need cleaned.</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {CARPET_ITEMS.map((it) => (
              <Field key={it.id} label={`${it.label} ($${it.price})`}>
                <input type="number" min={0} className="pg-input"
                  value={(form.details.items && form.details.items[it.id]) || ""}
                  onChange={(e) => {
                    const items = { ...(form.details.items || {}), [it.id]: e.target.value };
                    updateDetail("items", items);
                  }} />
              </Field>
            ))}
          </div>
        </div>
      )}
      {s === "post_construction" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SizeSelect optsKey="post_construction" labelText="Project size" />
          <FloorsPicker form={form} updateForm={updateForm} />
        </div>
      )}
      {s === "move" && <SizeSelect optsKey="move" labelText="Unit size" />}
    </div>
  );
}

function FloorsPicker({ form, updateForm }) {
  if (!FLOORS_APPLICABLE.has(form.service)) return null;
  return (
    <Field label="Number of floors">
      <select data-testid="detail-floors" className="pg-input" value={form.floors}
        onChange={(e) => updateForm({ floors: Number(e.target.value) })}>
        {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} {n === 1 ? "floor" : "floors"}</option>)}
      </select>
    </Field>
  );
}

function StepCondition({ form, updateForm }) {
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">Dirt / condition level</h3>
      <p className="text-sm text-[#64748B] mt-2">This affects pricing and crew size.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {CONDITIONS.map((c) => {
          const active = form.condition === c.id;
          return (
            <button key={c.id}
              data-testid={`condition-${c.id}`}
              onClick={() => updateForm({ condition: c.id })}
              className={`p-5 text-left rounded-sm border transition-all ${
                active ? "border-[#D4AF37] bg-[#FFFBEA]" : "border-[#E2E8F0] bg-white hover:border-[#D4AF37]"
              }`}
            >
              <div className="font-display text-base text-[#0A1128]">{c.label}</div>
              <div className="text-xs text-[#64748B] mt-1">{c.hint}</div>
            </button>
          );
        })}
      </div>
      {form.condition === "extreme" && (
        <div className="mt-6 p-4 bg-[#0A1128] text-[#F8FAFC] rounded-sm flex items-start gap-3">
          <AlertTriangle size={18} className="text-[#D4AF37] mt-0.5" />
          <div className="text-sm">
            Extreme condition jobs require a manual quote from our team after review. You can still submit
            your booking request — an admin will confirm the final price.
          </div>
        </div>
      )}

      <div className="mt-8">
        <h4 className="font-display text-base text-[#0A1128]">Urgency</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          {URGENCY_OPTIONS.map((u) => {
            const active = form.urgency === u.id;
            return (
              <button key={u.id}
                data-testid={`urgency-${u.id}`}
                onClick={() => updateForm({ urgency: u.id })}
                className={`p-4 text-left rounded-sm border text-sm transition-all ${
                  active ? "border-[#D4AF37] bg-[#FFFBEA]" : "border-[#E2E8F0] bg-white hover:border-[#D4AF37]"
                }`}
              >
                {u.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepAddons({ form, toggleAddon }) {
  const groups = ADDON_GROUPS_FOR_SERVICE[form.service] || [];
  if (groups.length === 0) {
    return (
      <div>
        <h3 className="font-display text-xl text-[#0A1128]">Add-ons</h3>
        <p className="text-sm text-[#64748B] mt-2">No optional add-ons for this service.</p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">Optional add-ons</h3>
      <p className="text-sm text-[#64748B] mt-2">Customize your booking with additional services.</p>
      <div className="mt-6 space-y-8">
        {groups.map((g) => (
          <div key={g}>
            <div className="eyebrow mb-3 capitalize">{g.replace("_", " ")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(ADDONS_CATALOG[g] || []).map((a) => {
                const active = form.addons.includes(a.id);
                return (
                  <button key={a.id}
                    data-testid={`addon-${a.id}`}
                    onClick={() => toggleAddon(a.id)}
                    className={`flex items-center justify-between p-4 rounded-sm border text-sm transition-all ${
                      active ? "border-[#D4AF37] bg-[#FFFBEA]" : "border-[#E2E8F0] bg-white hover:border-[#D4AF37]"
                    }`}
                  >
                    <span className="text-[#0A1128]">{a.label}</span>
                    <span className="text-[#D4AF37] font-semibold">+${a.price}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepPhotos({ form, photoRequired, handlePhotos, removePhoto }) {
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">
        Upload photos {photoRequired ? <span className="text-[#D4AF37] text-sm">(required)</span> : <span className="text-[#64748B] text-sm">(optional)</span>}
      </h3>
      <p className="text-sm text-[#64748B] mt-2">
        Photos help our team give you an accurate quote and prepare the right crew. Max 8 photos, 4MB each.
      </p>

      <label
        htmlFor="photo-upload"
        data-testid="photo-drop-area"
        className="mt-6 block border-2 border-dashed border-[#E2E8F0] rounded-sm p-10 text-center hover:border-[#D4AF37] transition cursor-pointer"
      >
        <input
          id="photo-upload"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="photo-upload-input"
          onChange={(e) => handlePhotos(e.target.files)}
        />
        <Upload className="mx-auto mb-3 text-[#D4AF37]" size={32} />
        <div className="text-sm text-[#0A1128] font-medium">Click to upload photos</div>
        <div className="text-xs text-[#64748B] mt-1">PNG, JPG up to 4MB each</div>
      </label>

      {form.photos.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {form.photos.map((p, i) => (
            <div key={i} className="relative group rounded-sm overflow-hidden border border-[#E2E8F0]">
              <img src={p.data} alt={p.name} className="w-full h-24 object-cover" />
              <button
                data-testid={`remove-photo-${i}`}
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 h-6 w-6 inline-flex items-center justify-center rounded-full bg-[#0A1128]/80 text-white opacity-0 group-hover:opacity-100 transition"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepSchedule({ form, updateForm }) {
  const today = new Date().toISOString().split("T")[0];
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">Preferred date & time</h3>
      <p className="text-sm text-[#64748B] mt-2">We’ll confirm the exact slot after review.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="Preferred date">
          <input
            type="date"
            min={today}
            className="pg-input"
            data-testid="schedule-date"
            value={form.preferred_date}
            onChange={(e) => updateForm({ preferred_date: e.target.value })}
          />
        </Field>
        <Field label="Preferred time">
          <select data-testid="schedule-time" className="pg-input" value={form.preferred_time}
            onChange={(e) => updateForm({ preferred_time: e.target.value })}>
            <option value="">Select time…</option>
            {["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

function StepContact({ form, updateForm }) {
  const c = form.contact;
  const setC = (k, v) => updateForm({ contact: { ...c, [k]: v } });
  return (
    <div>
      <h3 className="font-display text-xl text-[#0A1128]">Your contact details</h3>
      <p className="text-sm text-[#64748B] mt-2">We’ll reach out to confirm your booking.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="Full name">
          <input className="pg-input" data-testid="contact-name" value={c.name}
            onChange={(e) => setC("name", e.target.value)} />
        </Field>
        <Field label="Email">
          <input className="pg-input" type="email" data-testid="contact-email" value={c.email}
            onChange={(e) => setC("email", e.target.value)} />
        </Field>
        <Field label="Phone">
          <input className="pg-input" data-testid="contact-phone" value={c.phone}
            onChange={(e) => setC("phone", e.target.value)} />
        </Field>
        <Field label="Borough">
          <select className="pg-input" data-testid="contact-borough" value={c.borough}
            onChange={(e) => setC("borough", e.target.value)}>
            <option value="">Select…</option>
            {BOROUGHS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Service address">
            <input className="pg-input" data-testid="contact-address" value={c.address}
              onChange={(e) => setC("address", e.target.value)} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Notes (optional)">
            <textarea className="pg-input min-h-[80px]" data-testid="contact-notes" value={c.notes}
              onChange={(e) => setC("notes", e.target.value)} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepReview({ form, quote }) {
  const svc = SERVICES.find((s) => s.id === form.service);
  return (
    <div data-testid="review-section">
      <h3 className="font-display text-xl text-[#0A1128]">Review & confirm</h3>
      <p className="text-sm text-[#64748B] mt-2">Please double-check your booking request.</p>
      <dl className="mt-6 divide-y divide-[#E2E8F0] text-sm">
        <Row label="Service" value={svc?.label} />
        <Row label="Condition" value={form.condition} />
        <Row label="Floors" value={form.floors} />
        <Row label="Urgency" value={form.urgency.replace("_", " ")} />
        <Row label="Add-ons" value={form.addons.length ? form.addons.join(", ") : "None"} />
        <Row label="Photos" value={form.photos.length} />
        <Row label="Preferred" value={`${form.preferred_date} @ ${form.preferred_time}`} />
        <Row label="Customer" value={`${form.contact.name} · ${form.contact.email} · ${form.contact.phone}`} />
        <Row label="Address" value={`${form.contact.address} ${form.contact.borough ? `— ${form.contact.borough}` : ""}`} />
      </dl>

      {quote && (
        <div className="mt-8 p-6 bg-[#F8FAFC] rounded-sm border border-[#E2E8F0]">
          <div className="eyebrow mb-2">Estimated pricing</div>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-4xl text-[#0A1128]">${quote.total.toFixed(2)}</span>
            <span className="text-sm text-[#64748B]">total estimate</span>
          </div>
          <div className="mt-2 text-sm text-[#475569]">
            Required deposit (50%): <strong className="text-[#D4AF37]">${quote.deposit.toFixed(2)}</strong>
          </div>
        </div>
      )}

      <div className="mt-6 text-xs text-[#64748B] leading-relaxed">
        <Info size={14} className="inline mr-1 text-[#D4AF37]" /> 50% deposit is required to confirm
        your booking. Final price may increase based on actual condition, mold level, grease level, and
        complexity. Large or unsafe jobs require manual quote approval.
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium uppercase tracking-[0.15em] text-[#64748B] mb-2">{label}</div>
      {children}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between py-3 gap-4">
      <dt className="text-[#64748B]">{label}</dt>
      <dd className="text-[#0A1128] text-right font-medium capitalize">{value || "—"}</dd>
    </div>
  );
}

function QuoteSummary({ quote, loading, form }) {
  const svc = SERVICES.find((s) => s.id === form.service);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 lg:sticky lg:top-28">
      <div className="eyebrow mb-2">Quote summary</div>
      <div className="font-display text-lg text-[#0A1128]">{svc?.label || "Select a service"}</div>

      {!form.service && <p className="text-sm text-[#64748B] mt-4">Pick a service to see your quote.</p>}

      {form.service && (
        <>
          <div className="mt-5">
            {loading ? (
              <div className="font-display text-4xl text-[#CBD5E1]">…</div>
            ) : quote ? (
              <div className={`font-display text-5xl text-[#0A1128] price-pulse`} key={quote.total}>
                ${Number(quote.total).toFixed(0)}
              </div>
            ) : (
              <div className="font-display text-4xl text-[#CBD5E1]">—</div>
            )}
            <div className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] mt-1">Total estimate</div>
          </div>

          {quote && (
            <>
              <div className="mt-5 p-4 bg-[#0A1128] text-[#F8FAFC] rounded-sm">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37]">Deposit (50%)</div>
                <div className="font-display text-2xl mt-1">${Number(quote.deposit).toFixed(2)}</div>
                <div className="text-xs text-[#94A3B8] mt-1">Due to confirm booking</div>
              </div>

              <div className="mt-5 space-y-3 text-sm text-[#475569]">
                <div className="flex justify-between"><span>Base</span><span>${Number(quote.base_price).toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Add-ons</span><span>${Number(quote.addon_total).toFixed(2)}</span></div>
                {quote.condition_multiplier > 0 && (
                  <div className="flex justify-between"><span>Condition</span><span>+{(quote.condition_multiplier * 100).toFixed(0)}%</span></div>
                )}
                {quote.floor_multiplier > 0 && (
                  <div className="flex justify-between"><span>Floors</span><span>+{(quote.floor_multiplier * 100).toFixed(0)}%</span></div>
                )}
                {quote.urgency_multiplier > 0 && (
                  <div className="flex justify-between"><span>Urgency</span><span>+{(quote.urgency_multiplier * 100).toFixed(0)}%</span></div>
                )}
              </div>

              <div className="mt-5 pt-5 border-t border-[#E2E8F0] space-y-3">
                <div className="flex items-center gap-3 text-sm text-[#0A1128]">
                  <Users size={16} className="text-[#D4AF37]" />
                  {quote.estimated_workers} {quote.estimated_workers === 1 ? "worker" : "workers"}
                </div>
                <div className="flex items-center gap-3 text-sm text-[#0A1128]">
                  <Clock size={16} className="text-[#D4AF37]" />
                  {quote.estimated_hours_min}–{quote.estimated_hours_max} hours
                </div>
              </div>

              {quote.manual_quote_required && (
                <div className="mt-5 p-3 bg-[#FFFBEA] border border-[#D4AF37]/40 rounded-sm text-xs text-[#0A1128] flex items-start gap-2">
                  <AlertTriangle size={14} className="text-[#D4AF37] mt-0.5" />
                  Manual quote required — our team will review your request and contact you.
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
